import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowsLeftRight, CaretDown, Warning, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));
const today = () => new Date().toISOString().slice(0, 10);
const ddmmyyyy = (iso) => { const [y, m, d] = (iso || "").split("-"); return d && m && y ? `${d}-${m}-${y}` : iso; };

/**
 * v1.28.98 — Transfer Balance between two parties within the current tenant.
 * Accessible to Super User (with impersonation) and Company Admin. Creates
 * two linked ledger entries (debit on From, credit on To) sharing a single
 * transfer_ref_id so both rows can be traced together in the audit log.
 */
function PartyPicker({ parties, value, onChange, placeholder, testId, exclude }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const list = parties.filter((p) => p.id !== exclude);
    const filt = q.trim() ? list.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase())) : list;
    const sel = parties.find((p) => p.id === value);
    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
            <PopoverTrigger asChild>
                <button type="button" data-testid={testId} className="mt-1 inline-flex h-10 w-full items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 text-left text-sm font-medium text-slate-800 shadow-sm hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400">
                    <span className={`truncate ${sel ? "" : "text-slate-400"}`}>{sel?.name || placeholder}</span>
                    <CaretDown size={12} weight="bold" className="shrink-0 text-slate-400" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput data-testid={`${testId}-search`} placeholder="Type party name…" value={q} onValueChange={setQ} onKeyDown={(e) => {
                        // v1.29.09 — Fresh read at Enter time + prefer prefix over substring match. See Reports.jsx for full rationale.
                        if (e.key !== "Enter") return;
                        const qq = (e.currentTarget.value || "").trim().toLowerCase();
                        if (!qq) return;
                        e.preventDefault();
                        const pool = list;
                        const starts = pool.filter((p) => (p.name || "").toLowerCase().startsWith(qq));
                        const pick = starts.length ? starts : pool.filter((p) => (p.name || "").toLowerCase().includes(qq));
                        if (pick.length) { onChange(pick[0].id); setOpen(false); setQ(""); }
                    }} />
                    <CommandList>
                        <CommandEmpty>No matching party.</CommandEmpty>
                        <CommandGroup>
                            {filt.map((p) => (
                                <CommandItem key={p.id} value={p.name} data-testid={`${testId}-opt-${p.id}`} onSelect={() => { onChange(p.id); setOpen(false); setQ(""); }}>{p.name}</CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

function BalanceInfo({ balance, after, negativeWarn, testPrefix }) {
    if (balance === null || balance === undefined) return null;
    const neg = negativeWarn && after < 0;
    return (
        <div className="mt-1.5 space-y-0.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs" data-testid={`${testPrefix}-balance-box`}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-slate-500">Current Balance:</span>
                <span className="font-mono font-semibold text-slate-800" data-testid={`${testPrefix}-current-balance`}>{inr(balance)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
                <span className="text-slate-500">Balance After Transfer:</span>
                <span className={`font-mono font-semibold ${neg ? "text-rose-600" : "text-slate-800"}`} data-testid={`${testPrefix}-after-balance`}>{inr(after)}</span>
            </div>
            {neg && (
                <div className="flex items-center gap-1 text-[10px] font-semibold text-rose-600" data-testid={`${testPrefix}-negative-warning`}>
                    <Warning size={11} weight="fill" /> Goes negative after transfer
                </div>
            )}
        </div>
    );
}

export default function TransferBalance() {
    const [parties, setParties] = useState([]);
    const [fromId, setFromId] = useState("");
    const [toId, setToId] = useState("");
    const [amount, setAmount] = useState("");
    const [date, setDate] = useState(today());
    const [remarks, setRemarks] = useState("");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [fromBal, setFromBal] = useState(null);
    const [toBal, setToBal] = useState(null);
    const [audit, setAudit] = useState([]);
    const [needsCount, setNeedsCount] = useState(0);
    const [selected, setSelected] = useState(() => new Set());
    const [reversing, setReversing] = useState(false);
    const [confirmReverse, setConfirmReverse] = useState(false);
    const navigate = useNavigate();

    const loadAudit = async () => {
        try {
            const { data } = await api.get("/transactions/transfer-balance/audit");
            const list = data.transfers || [];
            setAudit(list);
            setNeedsCount(data.needs_correction_count || 0);
            // default-select every transfer that still needs correction
            setSelected(new Set(list.filter((t) => t.needs_correction).map((t) => t.transfer_ref_id)));
        } catch {
            setAudit([]);
            setNeedsCount(0);
        }
    };
    useEffect(() => { loadAudit(); }, []);

    const toggleSel = (ref) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(ref)) next.delete(ref); else next.add(ref);
        return next;
    });
    const toggleAll = () => setSelected((prev) => (
        prev.size === audit.length ? new Set() : new Set(audit.map((t) => t.transfer_ref_id))
    ));

    const runReverse = async () => {
        const refs = Array.from(selected);
        if (!refs.length) return toast.error("Select at least one transfer to reverse");
        setReversing(true);
        try {
            const { data } = await api.post("/transactions/transfer-balance/reverse", { ref_ids: refs });
            toast.success(`Corrected ${data.corrected} transfer(s) — balances now move the right way`);
            setConfirmReverse(false);
            await loadAudit();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setReversing(false); }
    };

    useEffect(() => {
        api.get("/parties").then((r) => setParties((r.data || []).filter((p) => p.active !== false))).catch(() => setParties([]));
    }, []);

    // Fetch current balance as soon as a party is picked in either dropdown.
    useEffect(() => {
        if (!fromId) { setFromBal(null); return; }
        let ok = true;
        api.get(`/ledger/${fromId}`).then((r) => { if (ok) setFromBal(Number(r.data?.closing_balance || 0)); }).catch(() => { if (ok) setFromBal(null); });
        return () => { ok = false; };
    }, [fromId]);
    useEffect(() => {
        if (!toId) { setToBal(null); return; }
        let ok = true;
        api.get(`/ledger/${toId}`).then((r) => { if (ok) setToBal(Number(r.data?.closing_balance || 0)); }).catch(() => { if (ok) setToBal(null); });
        return () => { ok = false; };
    }, [toId]);

    const fromParty = useMemo(() => parties.find((p) => p.id === fromId), [parties, fromId]);
    const toParty = useMemo(() => parties.find((p) => p.id === toId), [parties, toId]);
    const amt = Number(amount || 0);

    const buildPreview = async () => {
        if (!fromId || !toId) return toast.error("Pick both From and To parties");
        if (fromId === toId) return toast.error("From and To must be different parties");
        if (!amt || amt <= 0) return toast.error("Amount must be greater than zero");
        if (!remarks.trim()) return toast.error("Remarks are required");
        setBusy(true);
        try {
            const [fl, tl] = await Promise.all([
                api.get(`/ledger/${fromId}`),
                api.get(`/ledger/${toId}`),
            ]);
            setPreview({
                from: { id: fromId, name: fromParty?.name, before: fl.data.closing_balance, after: fl.data.closing_balance - amt },
                to:   { id: toId,   name: toParty?.name,   before: tl.data.closing_balance, after: tl.data.closing_balance + amt },
                amount: amt, date, remarks: remarks.trim(),
            });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const execute = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/transactions/transfer-balance", {
                from_party_id: fromId, to_party_id: toId, amount: amt, date, remarks: remarks.trim(),
            });
            toast.success(`Transferred ${inr(amt)} · Ref ${data.transfer_ref_id.slice(0, 8)}`);
            navigate(`/admin/reports?partyId=${fromId}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-2" data-testid="transfer-balance-page">
            <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><ArrowsLeftRight size={22} weight="duotone" /></div>
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Party Management</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Transfer Balance</h1>
                    <p className="mt-0.5 text-xs text-slate-500">Move an amount from one party's ledger to another. Both entries are linked by a Transfer Reference ID.</p>
                </div>
            </div>

            {!preview ? (
                <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">From Party (Debit)</Label>
                            <PartyPicker parties={parties} value={fromId} onChange={setFromId} placeholder="Pick source party" testId="tb-from" exclude={toId} />
                            <BalanceInfo balance={fromBal} after={(fromBal ?? 0) - amt} negativeWarn testPrefix="tb-from" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">To Party (Credit)</Label>
                            <PartyPicker parties={parties} value={toId} onChange={setToId} placeholder="Pick destination party" testId="tb-to" exclude={fromId} />
                            <BalanceInfo balance={toBal} after={(toBal ?? 0) + amt} testPrefix="tb-to" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Amount (₹)</Label>
                            <Input data-testid="tb-amount" type="number" min="0.01" step="0.01" className="mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Date</Label>
                            <Input data-testid="tb-date" type="date" className="mt-1" value={date} onChange={(e) => setDate(e.target.value)} />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Remarks</Label>
                            <Input data-testid="tb-remarks" className="mt-1" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g. Balance transfer from Jitu to Nitesh" />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button data-testid="tb-preview" onClick={buildPreview} disabled={busy} className="bg-indigo-700 text-white hover:bg-indigo-800">Preview Transfer</Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-4 rounded-lg border border-amber-300 bg-amber-50/40 p-5 shadow-sm" data-testid="tb-confirm">
                    <div className="flex items-center gap-2 text-amber-900">
                        <Warning size={16} weight="fill" />
                        <div className="font-display text-base font-bold">Confirm the transfer</div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="rounded-md border border-rose-200 bg-white p-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-rose-700">From · {preview.from.name} (Debit)</div>
                            <div className="mt-2 text-xs text-slate-600">Current balance</div>
                            <div className="font-mono text-lg font-bold text-slate-900" data-testid="tb-from-before">{inr(preview.from.before)}</div>
                            <div className="mt-2 text-xs text-slate-600">After transfer</div>
                            <div className="font-mono text-lg font-bold text-rose-800" data-testid="tb-from-after">{inr(preview.from.after)}</div>
                            <div className="mt-2 rounded bg-rose-50 px-2 py-1 font-mono text-[11px] font-semibold text-rose-900" data-testid="tb-from-beforeafter">
                                Before {inr(preview.from.before)} → After {inr(preview.from.after)} <span className="font-sans">(debit)</span>
                            </div>
                        </div>
                        <div className="rounded-md border border-emerald-200 bg-white p-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">To · {preview.to.name} (Credit)</div>
                            <div className="mt-2 text-xs text-slate-600">Current balance</div>
                            <div className="font-mono text-lg font-bold text-slate-900" data-testid="tb-to-before">{inr(preview.to.before)}</div>
                            <div className="mt-2 text-xs text-slate-600">After transfer</div>
                            <div className="font-mono text-lg font-bold text-emerald-800" data-testid="tb-to-after">{inr(preview.to.after)}</div>
                            <div className="mt-2 rounded bg-emerald-50 px-2 py-1 font-mono text-[11px] font-semibold text-emerald-900" data-testid="tb-to-beforeafter">
                                Before {inr(preview.to.before)} → After {inr(preview.to.after)} <span className="font-sans">(credit)</span>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 text-xs">
                        <div><b>Amount:</b> <span className="font-mono text-sm font-bold text-slate-900">{inr(preview.amount)}</span></div>
                        <div className="mt-1"><b>Date:</b> {ddmmyyyy(preview.date)}</div>
                        <div className="mt-1"><b>Remarks:</b> {preview.remarks}</div>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        <Button data-testid="tb-back" variant="outline" onClick={() => setPreview(null)} disabled={busy}>Back to edit</Button>
                        <Button data-testid="tb-confirm-execute" onClick={execute} disabled={busy} className="bg-amber-700 text-white hover:bg-amber-800">
                            <CheckCircle size={14} weight="fill" className="mr-1.5" />{busy ? "Transferring…" : "Confirm & Transfer"}
                        </Button>
                    </div>
                </div>
            )}

            <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="tb-audit-panel">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h2 className="font-display text-lg font-bold text-slate-900">Review past transfers</h2>
                        <p className="text-xs text-slate-500">
                            Earlier transfers moved balances the wrong way. Review and reverse the affected ones — the source will be debited (balance ↓) and the destination credited (balance ↑).
                        </p>
                    </div>
                    {needsCount > 0 && (
                        <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-bold text-rose-700" data-testid="tb-audit-needs-count">
                            {needsCount} need correction
                        </span>
                    )}
                </div>

                {audit.length === 0 ? (
                    <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500" data-testid="tb-audit-empty">
                        No balance transfers found.
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[640px] text-left text-xs">
                                <thead>
                                    <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                                        <th className="py-2 pr-2">
                                            <input type="checkbox" data-testid="tb-audit-select-all"
                                                checked={audit.length > 0 && selected.size === audit.length}
                                                onChange={toggleAll} />
                                        </th>
                                        <th className="py-2 pr-2">Date</th>
                                        <th className="py-2 pr-2">From → To</th>
                                        <th className="py-2 pr-2 text-right">Amount</th>
                                        <th className="py-2 pr-2 text-right">From / To sign</th>
                                        <th className="py-2 pr-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {audit.map((t) => (
                                        <tr key={t.transfer_ref_id} className="border-b border-slate-100" data-testid={`tb-audit-row-${t.transfer_ref_id.slice(0, 8)}`}>
                                            <td className="py-2 pr-2">
                                                <input type="checkbox" data-testid={`tb-audit-check-${t.transfer_ref_id.slice(0, 8)}`}
                                                    checked={selected.has(t.transfer_ref_id)}
                                                    onChange={() => toggleSel(t.transfer_ref_id)} />
                                            </td>
                                            <td className="py-2 pr-2 whitespace-nowrap text-slate-700">{ddmmyyyy(t.date)}</td>
                                            <td className="py-2 pr-2 text-slate-800">
                                                <span className="font-medium">{t.from_party_name}</span>
                                                <span className="mx-1 text-slate-400">→</span>
                                                <span className="font-medium">{t.to_party_name}</span>
                                                {t.remarks ? <div className="text-[10px] text-slate-400">{t.remarks}</div> : null}
                                            </td>
                                            <td className="py-2 pr-2 text-right font-mono font-bold text-slate-900">{inr(t.amount)}</td>
                                            <td className="py-2 pr-2 text-right font-mono text-[11px]">
                                                <span className={t.from_signed > 0 ? "text-rose-700" : "text-emerald-700"}>{t.from_signed > 0 ? "+" : ""}{t.from_signed}</span>
                                                <span className="mx-1 text-slate-300">/</span>
                                                <span className={t.to_signed > 0 ? "text-rose-700" : "text-emerald-700"}>{t.to_signed > 0 ? "+" : ""}{t.to_signed}</span>
                                            </td>
                                            <td className="py-2 pr-2">
                                                {t.needs_correction ? (
                                                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">Needs correction</span>
                                                ) : (
                                                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">OK</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {!confirmReverse ? (
                            <div className="flex justify-end">
                                <Button data-testid="tb-audit-reverse" onClick={() => setConfirmReverse(true)} disabled={selected.size === 0}
                                    className="bg-rose-700 text-white hover:bg-rose-800">
                                    <ArrowClockwise size={14} weight="bold" className="mr-1.5" />Reverse selected ({selected.size})
                                </Button>
                            </div>
                        ) : (
                            <div className="rounded-md border border-rose-300 bg-rose-50 p-3" data-testid="tb-audit-confirm">
                                <div className="flex items-center gap-2 text-rose-900">
                                    <Warning size={16} weight="fill" />
                                    <div className="text-sm font-bold">Reverse {selected.size} transfer(s)?</div>
                                </div>
                                <p className="mt-1 text-xs text-rose-800">
                                    Each selected transfer's source party will be debited (balance decreases) and the destination credited (balance increases). This updates the two linked ledger rows in place and is idempotent.
                                </p>
                                <div className="mt-2 flex justify-end gap-2">
                                    <Button data-testid="tb-audit-cancel" variant="outline" onClick={() => setConfirmReverse(false)} disabled={reversing}>Cancel</Button>
                                    <Button data-testid="tb-audit-confirm-run" onClick={runReverse} disabled={reversing} className="bg-rose-700 text-white hover:bg-rose-800">
                                        {reversing ? "Reversing…" : "Confirm reverse"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
